/**
 * Serviço de Monitoramento de Saúde das Integrações
 * Responsável por validar, monitorar e recuperar conexões automaticamente
 */

import {
  getAccountsApiV1IntegrationsInstagramInsightsAccountsGet,
  getAccountsApiV1IntegrationsMetaAdsAccountsGet,
  getAccountsApiV1IntegrationsSearchConsoleAccountsGet,
  getCompleteDataApiV1IntegrationsGoogleAnalyticsCompleteDataGet,
  getCompleteDataApiV1IntegrationsInstagramInsightsCompleteDataGet,
  getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet,
  getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet,
  getConnectionStatusApiV1IntegrationsSearchConsoleConnectionStatusGet,
  getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet,
  getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet,
  getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet,
  getGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsAccountsGet,
  getGoogleAnalyticsStatusApiV1IntegrationsGoogleAnalyticsStatusGet,
  getStatusApiV1IntegrationsInstagramInsightsStatusGet,
  getTopQueriesApiV1IntegrationsSearchConsoleTopQueriesGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  GoogleAdsConnectionStatus,
  GoogleAnalyticsConnectionStatus,
  InstagramConnectionStatus,
  MetaAdsConnectionStatus,
  SearchConsoleConnectionStatus,
} from "@/backend-sdk/client/types.gen";

export type IntegrationType =
  | "google_ads"
  | "google_analytics"
  | "meta_ads"
  | "search_console"
  | "instagram_insights";

export type ConnectionStatus =
  | "connected"
  | "disconnected"
  | "pending"
  | "error"
  | "degraded";

type HealthSeverity = "critical" | "warning" | "info";

export interface HealthIssue {
  id: string;
  severity: HealthSeverity;
  message: string;
  description?: string;
  action?: string;
  autofix?: boolean;
  timestamp: string;
  resolved?: boolean;
}

export interface ConnectionHealth {
  score: number; // 0-100
  status: ConnectionStatus;
  issues: HealthIssue[];
  lastCheck: string;
  lastSync?: string;
  dataQuality?: number; // 0-100
  uptime?: number; // percentage
  is_connected?: boolean; // Indica se a integração está conectada na API
}

interface ValidationResult {
  isValid: boolean;
  score: number;
  issues: HealthIssue[];
  suggestions: string[];
}

type HealthMetricRecord = {
  account_id?: string | number | null;
  campaign_id?: string | number | null;
  campaign_name?: string | null;
  keyword_id?: string | null;
  keyword_text?: string | null;
  status?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  cost?: number | null;
  conversions?: number | null;
  ctr?: number | null;
  cpc?: number | null;
  roas?: number | null;
  quality_score?: number | null;
  impression_share?: number | null;
  users?: number | null;
  sessions?: number | null;
  pageviews?: number | null;
  revenue?: number | null;
  transactions?: number | null;
  bounce_rate?: number | null;
  session_duration?: number | null;
  pages_per_session?: number | null;
  device_category?: string | null;
  position?: number | null;
  indexed_pages?: number | null;
  reach?: number | null;
  engagement?: number | null;
  engagement_rate?: number | null;
  stories_completion_rate?: number | null;
  core_web_vitals?: {
    lcp?: number | null;
    fid?: number | null;
    cls?: number | null;
    status?: string | null;
  } | null;
  spend?: number | null;
  objective?: string | null;
  frequency?: number | null;
  adset_id?: string | number | null;
  adset_name?: string | null;
  cost_per_result?: number | null;
  relevance_score?: number | null;
  ad_id?: string | number | null;
  placement_breakdown?: PlacementPerformance[] | null;
  demographics_breakdown?: DemographicPerformance[] | null;
  query?: string | null;
  page?: string | null;
  indexing_status?: string | null;
  position_change?: number | null;
  mobile_usability_issues?: string[] | null;
  followers_count?: number | null;
  followers_change?: number | null;
  post_type?: string | null;
  hashtags?: string[] | null;
  post_id?: string | number | null;
  story_id?: string | number | null;
  saves?: number | null;
  likes?: number | null;
  comments?: number | null;
  plays?: number | null;
  shares?: number | null;
  post_hour?: number | null;
  days_since_last_post?: number | null;
};

type PlacementPerformance = {
  placement?: string | null;
  ctr?: number | null;
};

type DemographicPerformance = {
  age_range?: string | null;
  ctr?: number | null;
};

type IntegrationStatusResponse =
  | GoogleAdsConnectionStatus
  | GoogleAnalyticsConnectionStatus
  | MetaAdsConnectionStatus
  | SearchConsoleConnectionStatus
  | InstagramConnectionStatus;

interface IntegrationMetrics {
  totalRequests: number;
  successRate: number;
  averageResponseTime: number;
  lastError?: string;
  errorCount: number;
}

// Estratégias de recovery por tipo de erro
const RECOVERY_STRATEGIES = {
  connection_lost: () => exponentialBackoffRetry(),
  rate_limited: () => respectRateLimitAndRetry(),
  token_expired: () => refreshTokenAndRetry(),
  data_inconsistent: () => forceSyncAndValidate(),
  permission_denied: () => redirectToReauth(),
  api_error: () => retryWithDelay(),
};

// Thresholds para validação de dados por integração
const VALIDATION_THRESHOLDS = {
  google_ads: {
    minCtr: 0.01, // 0.01%
    maxCpc: 50, // R$ 50
    minRoas: 1.5,
    maxCampaigns: 1000,
    dataFreshness: 24 * 60 * 60 * 1000, // 24 horas
  },
  google_analytics: {
    minBounceRate: 0.1, // 10%
    maxBounceRate: 0.9, // 90%
    minSessionDuration: 10, // 10 segundos
    maxUsersToSessions: 2, // proporção usuários/sessões
    dataFreshness: 24 * 60 * 60 * 1000, // 24 horas
  },
  meta_ads: {
    minEngagementRate: 0.01, // 1%
    maxCostPerResult: 20, // R$ 20
    minReachToImpressions: 0.5, // 50%
    dataFreshness: 24 * 60 * 60 * 1000, // 24 horas
  },
  search_console: {
    minCtr: 0.005, // 0.5%
    maxPosition: 20,
    minIndexedPages: 10,
    dataFreshness: 3 * 24 * 60 * 60 * 1000, // 3 dias (Search Console delay)
  },
  instagram_insights: {
    minEngagementRate: 0.01, // 1%
    minReachToImpressions: 0.3, // 30%
    minStoriesCompletionRate: 0.7, // 70%
    dataFreshness: 24 * 60 * 60 * 1000, // 24 horas
  },
};

type ValidationThresholds = typeof VALIDATION_THRESHOLDS;

class IntegrationHealthService {
  private healthCache = new Map<string, ConnectionHealth>();
  private monitoringIntervals = new Map<string, NodeJS.Timeout>();
  private metricsCache = new Map<string, IntegrationMetrics>();

  /**
   * Monitora saúde de uma integração específica
   */
  async monitorIntegration(
    integrationType: IntegrationType,
    integrationId: string,
    intervalMs: number = 30000, // 30 segundos
  ): Promise<void> {
    // Parar monitoramento anterior se existir
    this.stopMonitoring(integrationId);

    const checkHealth = async () => {
      try {
        const health = await this.checkIntegrationHealth(
          integrationType,
          integrationId,
        );
        this.healthCache.set(integrationId, health);

        // Auto-recovery para problemas críticos
        if (health.score < 50) {
          await this.attemptAutoRecovery(
            integrationType,
            integrationId,
            health.issues,
          );
        }

        // Emit event for listeners
        this.emitHealthUpdate(integrationId, health);
      } catch (error) {
        console.error(`Erro ao monitorar integração ${integrationId}:`, error);
      }
    };

    // Check inicial
    await checkHealth();

    // Configurar monitoramento contínuo
    const interval = setInterval(checkHealth, intervalMs);
    this.monitoringIntervals.set(integrationId, interval);
  }

  /**
   * Para o monitoramento de uma integração
   */
  stopMonitoring(integrationId: string): void {
    const interval = this.monitoringIntervals.get(integrationId);
    if (interval) {
      clearInterval(interval);
      this.monitoringIntervals.delete(integrationId);
    }
  }

  /**
   * Verifica a saúde atual de uma integração
   */
  async checkIntegrationHealth(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ConnectionHealth> {
    const issues: HealthIssue[] = [];
    let score = 100;
    let status: ConnectionStatus = "connected";
    let is_connected = false;

    try {
      // Verificar conectividade básica
      const connectivityResult = await this.checkConnectivity(
        integrationType,
        integrationId,
      );
      is_connected = connectivityResult.isValid; // Preservar informação de conectividade

      if (!connectivityResult.isValid) {
        issues.push(...connectivityResult.issues);
        score = Math.min(score, connectivityResult.score);
        status = "error";
      }

      // Verificar qualidade dos dados
      const dataQualityResult = await this.validateDataQuality(
        integrationType,
        integrationId,
      );
      if (!dataQualityResult.isValid) {
        issues.push(...dataQualityResult.issues);
        score = Math.min(score, dataQualityResult.score);
        if (status === "connected") status = "degraded";
      }

      // Verificar freshness dos dados
      const freshnessResult = await this.checkDataFreshness(
        integrationType,
        integrationId,
      );
      if (!freshnessResult.isValid) {
        issues.push(...freshnessResult.issues);
        score = Math.min(score, freshnessResult.score);
        if (status === "connected") status = "degraded";
      }

      // Verificar performance
      const performanceResult = await this.checkPerformance(
        integrationType,
        integrationId,
      );
      if (!performanceResult.isValid) {
        issues.push(...performanceResult.issues);
        score = Math.min(score, performanceResult.score);
      }
    } catch (error) {
      issues.push({
        id: `health-check-error-${Date.now()}`,
        severity: "critical",
        message: "Erro ao verificar saúde da integração",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        timestamp: new Date().toISOString(),
      });
      score = 0;
      status = "error";
      is_connected = false;
    }

    return {
      score,
      status,
      issues,
      lastCheck: new Date().toISOString(),
      dataQuality: await this.calculateDataQuality(
        integrationType,
        integrationId,
      ),
      uptime: await this.calculateUptime(integrationId),
      is_connected,
    };
  }

  /**
   * Valida qualidade dos dados de uma integração específica
   */
  async validateDataQuality(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ValidationResult> {
    const issues: HealthIssue[] = [];
    let score = 100;
    const suggestions: string[] = [];

    const thresholds = VALIDATION_THRESHOLDS[integrationType];
    if (!thresholds) {
      return { isValid: true, score: 100, issues: [], suggestions: [] };
    }

    try {
      // Buscar dados recentes para validação
      const data = await this.fetchRecentData(integrationType, integrationId);

      if (!data || data.length === 0) {
        issues.push({
          id: `no-data-${Date.now()}`,
          severity: "critical",
          message: "Nenhum dado encontrado",
          description: "A integração não possui dados para validação",
          action: "Verificar sincronização",
          timestamp: new Date().toISOString(),
        });
        return { isValid: false, score: 0, issues, suggestions };
      }

      // Validações específicas por tipo de integração
      switch (integrationType) {
        case "google_ads":
          await this.validateGoogleAdsData(
            data,
            issues,
            suggestions,
            VALIDATION_THRESHOLDS.google_ads,
          );
          break;
        case "google_analytics":
          await this.validateAnalyticsData(
            data,
            issues,
            suggestions,
            VALIDATION_THRESHOLDS.google_analytics,
          );
          break;
        case "meta_ads":
          await this.validateMetaAdsData(
            data,
            issues,
            suggestions,
            VALIDATION_THRESHOLDS.meta_ads,
          );
          break;
        case "search_console":
          await this.validateSearchConsoleData(
            data,
            issues,
            suggestions,
            VALIDATION_THRESHOLDS.search_console,
          );
          break;
        case "instagram_insights":
          await this.validateInstagramData(
            data,
            issues,
            suggestions,
            VALIDATION_THRESHOLDS.instagram_insights,
          );
          break;
      }

      // Calcular score baseado em issues
      const criticalIssues = issues.filter(
        (i) => i.severity === "critical",
      ).length;
      const warningIssues = issues.filter(
        (i) => i.severity === "warning",
      ).length;

      score = Math.max(0, score - criticalIssues * 30 - warningIssues * 10);
    } catch (error) {
      issues.push({
        id: `validation-error-${Date.now()}`,
        severity: "critical",
        message: "Erro na validação dos dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        timestamp: new Date().toISOString(),
      });
      score = 0;
    }

    return {
      isValid: score >= 70,
      score,
      issues,
      suggestions,
    };
  }

  /**
   * Tentativa de recuperação automática
   */
  private async attemptAutoRecovery(
    integrationType: IntegrationType,
    integrationId: string,
    issues: HealthIssue[],
  ): Promise<void> {
    console.log(
      `🔄 Tentando recuperação automática para ${integrationType}:${integrationId}`,
    );

    for (const issue of issues) {
      if (issue.autofix && issue.severity === "critical") {
        try {
          // Identificar tipo de erro e aplicar estratégia
          const errorType = this.identifyErrorType(issue);
          const strategy =
            RECOVERY_STRATEGIES[errorType as keyof typeof RECOVERY_STRATEGIES];

          if (strategy) {
            console.log(`🛠️ Aplicando estratégia de recovery: ${errorType}`);
            await strategy();

            // Marcar issue como resolvido
            issue.resolved = true;

            console.log(
              `✅ Recovery automático bem-sucedido para: ${issue.message}`,
            );
          }
        } catch (error) {
          console.error(`❌ Falha no recovery automático:`, error);
        }
      }
    }
  }

  /**
   * Identifica o tipo de erro para aplicar estratégia correta
   */
  private identifyErrorType(issue: HealthIssue): string {
    const message = issue.message.toLowerCase();

    if (message.includes("conexão") || message.includes("connection")) {
      return "connection_lost";
    }
    if (message.includes("rate limit") || message.includes("quota")) {
      return "rate_limited";
    }
    if (message.includes("token") || message.includes("auth")) {
      return "token_expired";
    }
    if (message.includes("inconsistent") || message.includes("invalid")) {
      return "data_inconsistent";
    }
    if (message.includes("permission") || message.includes("forbidden")) {
      return "permission_denied";
    }

    return "api_error";
  }

  /**
   * Validações específicas para Google Ads
   */
  private async validateGoogleAdsData(
    data: HealthMetricRecord[],
    issues: HealthIssue[],
    suggestions: string[],
    thresholds: ValidationThresholds["google_ads"],
  ): Promise<void> {
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let activeCampaigns = 0;
    let pausedCampaigns = 0;
    const qualityScores: number[] = [];

    // Análise agregada dos dados
    for (const record of data) {
      totalImpressions += record.impressions || 0;
      totalClicks += record.clicks || 0;
      totalCost += record.cost || 0;
      totalConversions += record.conversions || 0;

      if (record.status === "ENABLED") activeCampaigns++;
      if (record.status === "PAUSED") pausedCampaigns++;

      if (record.quality_score) qualityScores.push(record.quality_score);

      // Validações por registro individual
      if (record.ctr && record.ctr < thresholds.minCtr) {
        issues.push({
          id: `low-ctr-${record.campaign_id || Date.now()}`,
          severity: "warning",
          message: `CTR muito baixo: ${(record.ctr * 100).toFixed(2)}%`,
          description: `Campanha: ${record.campaign_name || "Desconhecida"}`,
          action: "Revisar palavras-chave e anúncios",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Considere otimizar palavras-chave e textos dos anúncios",
        );
      }

      if (record.cpc && record.cpc > thresholds.maxCpc) {
        issues.push({
          id: `high-cpc-${record.campaign_id || Date.now()}`,
          severity: "warning",
          message: `CPC muito alto: R$ ${record.cpc.toFixed(2)}`,
          description: `Campanha: ${record.campaign_name || "Desconhecida"}`,
          action: "Revisar lances e estratégias",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Analise a concorrência e ajuste os lances");
      }

      if (record.roas && record.roas < thresholds.minRoas) {
        issues.push({
          id: `low-roas-${record.campaign_id || Date.now()}`,
          severity: "critical",
          message: `ROAS abaixo do esperado: ${record.roas.toFixed(1)}x`,
          description: `Campanha: ${record.campaign_name || "Desconhecida"}`,
          action: "Otimizar campanhas urgentemente",
          autofix: false,
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Revise segmentação e ofertas das campanhas");
      }

      // Validação de Quality Score
      if (record.quality_score && record.quality_score < 6) {
        issues.push({
          id: `low-quality-score-${record.keyword_id || Date.now()}`,
          severity: "warning",
          message: `Quality Score baixo: ${record.quality_score}/10`,
          description: `Palavra-chave: ${record.keyword_text || "Desconhecida"}`,
          action: "Melhorar relevância dos anúncios",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Otimize a relevância entre palavras-chave, anúncios e landing pages",
        );
      }

      // Validação de Impression Share
      if (record.impression_share && record.impression_share < 0.5) {
        issues.push({
          id: `low-impression-share-${record.campaign_id || Date.now()}`,
          severity: "info",
          message: `Impression Share baixo: ${(record.impression_share * 100).toFixed(1)}%`,
          description: `Campanha: ${record.campaign_name || "Desconhecida"}`,
          action: "Considerar aumentar orçamento",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Aumente o orçamento ou melhore os Quality Scores");
      }
    }

    // Validações agregadas
    const avgQualityScore =
      qualityScores.length > 0
        ? qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length
        : 0;

    if (avgQualityScore > 0 && avgQualityScore < 7) {
      issues.push({
        id: `overall-low-quality-${Date.now()}`,
        severity: "critical",
        message: `Quality Score médio baixo: ${avgQualityScore.toFixed(1)}/10`,
        action: "Revisão geral necessária",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Execute uma auditoria completa das palavras-chave e anúncios",
      );
    }

    if (pausedCampaigns > activeCampaigns * 0.3) {
      issues.push({
        id: `many-paused-campaigns-${Date.now()}`,
        severity: "warning",
        message: `Muitas campanhas pausadas: ${pausedCampaigns} de ${activeCampaigns + pausedCampaigns}`,
        action: "Revisar campanhas pausadas",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Analise se campanhas pausadas podem ser reativadas ou otimizadas",
      );
    }

    // Validação de performance geral
    const overallCtr =
      totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const overallCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? totalConversions / totalClicks : 0;

    if (overallCtr < thresholds.minCtr) {
      issues.push({
        id: `overall-low-ctr-${Date.now()}`,
        severity: "critical",
        message: `CTR geral baixo: ${(overallCtr * 100).toFixed(2)}%`,
        action: "Auditoria geral de anúncios",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Realize uma auditoria completa dos anúncios e extensões",
      );
    }

    if (conversionRate < 0.01) {
      // Menos de 1% de conversão
      issues.push({
        id: `low-conversion-rate-${Date.now()}`,
        severity: "critical",
        message: `Taxa de conversão muito baixa: ${(conversionRate * 100).toFixed(2)}%`,
        action: "Otimizar landing pages e ofertas",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Revise landing pages, ofertas e configuração de conversões",
      );
    }
  }

  /**
   * Validações específicas para Google Analytics
   */
  private async validateAnalyticsData(
    data: HealthMetricRecord[],
    issues: HealthIssue[],
    suggestions: string[],
    thresholds: ValidationThresholds["google_analytics"],
  ): Promise<void> {
    let totalSessions = 0;
    let totalUsers = 0;
    let totalPageviews = 0;
    let totalRevenue = 0;
    let totalTransactions = 0;
    const bounceRates: number[] = [];
    const sessionDurations: number[] = [];
    const pagesPerSession: number[] = [];

    // Análise agregada dos dados
    for (const record of data) {
      totalSessions += record.sessions || 0;
      totalUsers += record.users || 0;
      totalPageviews += record.pageviews || 0;
      totalRevenue += record.revenue || 0;
      totalTransactions += record.transactions || 0;

      if (record.bounce_rate) bounceRates.push(record.bounce_rate);
      if (record.session_duration)
        sessionDurations.push(record.session_duration);
      if (record.pages_per_session)
        pagesPerSession.push(record.pages_per_session);

      // Validações por registro individual
      if (record.bounce_rate) {
        if (record.bounce_rate < thresholds.minBounceRate) {
          issues.push({
            id: `bounce-rate-low-${Date.now()}`,
            severity: "info",
            message: `Taxa de rejeição muito baixa: ${(record.bounce_rate * 100).toFixed(1)}%`,
            description:
              "Pode indicar problemas de tracking ou configuração incorreta",
            action: "Verificar configuração do GA",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Revise a configuração do Google Analytics e eventos de tracking",
          );
        }

        if (record.bounce_rate > thresholds.maxBounceRate) {
          issues.push({
            id: `bounce-rate-high-${Date.now()}`,
            severity: "warning",
            message: `Taxa de rejeição muito alta: ${(record.bounce_rate * 100).toFixed(1)}%`,
            description:
              "Indica possível problema de qualidade ou relevância do conteúdo",
            action: "Analisar qualidade do conteúdo",
            timestamp: new Date().toISOString(),
          });
          suggestions.push("Melhore a relevância e velocidade das páginas");
        }
      }

      if (
        record.session_duration &&
        record.session_duration < thresholds.minSessionDuration
      ) {
        issues.push({
          id: `short-sessions-${Date.now()}`,
          severity: "warning",
          message: `Duração de sessão muito baixa: ${record.session_duration}s`,
          description: "Usuários estão saindo muito rapidamente",
          action: "Melhorar engajamento do site",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Analise a experiência do usuário e navegação");
      }

      // Validação de Core Web Vitals (se disponível)
      if (record.core_web_vitals) {
        const lcp = record.core_web_vitals.lcp;
        const fid = record.core_web_vitals.fid;
        const cls = record.core_web_vitals.cls;

        if (lcp != null && lcp > 2.5) {
          issues.push({
            id: `poor-lcp-${Date.now()}`,
            severity: "critical",
            message: `LCP muito alto: ${lcp.toFixed(1)}s`,
            description: "Largest Contentful Paint acima do recomendado",
            action: "Otimizar velocidade de carregamento",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Otimize imagens, CSS e JavaScript para melhorar o LCP",
          );
        }

        if (fid != null && fid > 100) {
          issues.push({
            id: `poor-fid-${Date.now()}`,
            severity: "warning",
            message: `FID alto: ${fid}ms`,
            description: "First Input Delay acima do recomendado",
            action: "Otimizar JavaScript",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Minimize e otimize o JavaScript para reduzir o FID",
          );
        }

        if (cls != null && cls > 0.1) {
          issues.push({
            id: `poor-cls-${Date.now()}`,
            severity: "warning",
            message: `CLS alto: ${cls.toFixed(3)}`,
            description: "Cumulative Layout Shift acima do recomendado",
            action: "Estabilizar layout",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Adicione dimensões às imagens e evite inserção dinâmica de conteúdo",
          );
        }
      }

      // Validação de conversão de e-commerce
      if (record.sessions && record.transactions) {
        const conversionRate = record.transactions / record.sessions;
        if (conversionRate < 0.01) {
          // Menos de 1%
          issues.push({
            id: `low-ecommerce-conversion-${Date.now()}`,
            severity: "warning",
            message: `Taxa de conversão e-commerce baixa: ${(conversionRate * 100).toFixed(2)}%`,
            action: "Otimizar funil de vendas",
            timestamp: new Date().toISOString(),
          });
          suggestions.push("Analise o funil de vendas e otimize o checkout");
        }
      }

      // Validação de engajamento mobile vs desktop
      if (record.device_category) {
        const bounceRate = record.bounce_rate;
        if (
          record.device_category === "mobile" &&
          bounceRate != null &&
          bounceRate > 0.7
        ) {
          issues.push({
            id: `mobile-high-bounce-${Date.now()}`,
            severity: "critical",
            message: `Taxa de rejeição mobile muito alta: ${(bounceRate * 100).toFixed(1)}%`,
            description: "Experiência mobile precisa de atenção",
            action: "Otimizar experiência mobile",
            timestamp: new Date().toISOString(),
          });
          suggestions.push("Melhore a responsividade e velocidade mobile");
        }
      }
    }

    // Validações agregadas
    const avgBounceRate =
      bounceRates.length > 0
        ? bounceRates.reduce((a, b) => a + b, 0) / bounceRates.length
        : 0;

    const avgSessionDuration =
      sessionDurations.length > 0
        ? sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length
        : 0;

    const avgPagesPerSession =
      pagesPerSession.length > 0
        ? pagesPerSession.reduce((a, b) => a + b, 0) / pagesPerSession.length
        : 0;

    // Análise de tendências
    if (totalSessions > 0 && totalUsers > 0) {
      const sessionsPerUser = totalSessions / totalUsers;
      if (sessionsPerUser < 1.2) {
        issues.push({
          id: `low-user-retention-${Date.now()}`,
          severity: "warning",
          message: `Baixa retenção de usuários: ${sessionsPerUser.toFixed(2)} sessões por usuário`,
          action: "Melhorar retenção",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Implemente estratégias de remarketing e melhore a experiência do usuário",
        );
      }
    }

    // Validação de performance geral
    if (avgPagesPerSession < 2) {
      issues.push({
        id: `low-pages-per-session-${Date.now()}`,
        severity: "warning",
        message: `Poucas páginas por sessão: ${avgPagesPerSession.toFixed(1)}`,
        action: "Melhorar navegação interna",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Melhore a navegação interna e links relacionados");
    }

    // Validação de receita (se e-commerce ativo)
    if (totalRevenue > 0 && totalTransactions > 0) {
      const avgOrderValue = totalRevenue / totalTransactions;
      if (avgOrderValue < 50) {
        // Threshold básico
        issues.push({
          id: `low-avg-order-value-${Date.now()}`,
          severity: "info",
          message: `Valor médio de pedido baixo: R$ ${avgOrderValue.toFixed(2)}`,
          action: "Implementar upselling",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Implemente estratégias de upselling e cross-selling");
      }
    }

    // Validação de qualidade de tráfego
    if (avgBounceRate > 0.8) {
      issues.push({
        id: `overall-high-bounce-${Date.now()}`,
        severity: "critical",
        message: `Taxa de rejeição geral muito alta: ${(avgBounceRate * 100).toFixed(1)}%`,
        action: "Auditoria completa necessária",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Realize uma auditoria completa da experiência do usuário",
      );
    }
  }

  /**
   * Validações específicas para Meta Ads
   */
  private async validateMetaAdsData(
    data: HealthMetricRecord[],
    issues: HealthIssue[],
    suggestions: string[],
    thresholds: ValidationThresholds["meta_ads"],
  ): Promise<void> {
    let totalSpend = 0;
    let totalImpressions = 0;
    let totalReach = 0;
    let totalClicks = 0;
    let totalConversions = 0;
    const engagementRates: number[] = [];
    const adSetsByObjective: { [key: string]: number } = {};
    const creativeFatigue: number[] = [];

    // Análise agregada dos dados
    for (const record of data) {
      totalSpend += record.spend || 0;
      totalImpressions += record.impressions || 0;
      totalReach += record.reach || 0;
      totalClicks += record.clicks || 0;
      totalConversions += record.conversions || 0;

      if (record.engagement_rate) engagementRates.push(record.engagement_rate);
      if (record.objective) {
        adSetsByObjective[record.objective] =
          (adSetsByObjective[record.objective] || 0) + 1;
      }

      // Cálculo de fadiga de criativo (frequência alta)
      if (record.frequency) creativeFatigue.push(record.frequency);

      // Validações por registro individual
      if (
        record.engagement_rate &&
        record.engagement_rate < thresholds.minEngagementRate
      ) {
        issues.push({
          id: `low-engagement-${record.adset_id || Date.now()}`,
          severity: "warning",
          message: `Taxa de engajamento baixa: ${(record.engagement_rate * 100).toFixed(2)}%`,
          description: `Conjunto de anúncios: ${record.adset_name || "Desconhecido"}`,
          action: "Revisar criativos e audiência",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Teste novos formatos de anúncio e audiências");
      }

      if (
        record.cost_per_result &&
        record.cost_per_result > thresholds.maxCostPerResult
      ) {
        issues.push({
          id: `high-cost-${record.adset_id || Date.now()}`,
          severity: "critical",
          message: `Custo por resultado alto: R$ ${record.cost_per_result.toFixed(2)}`,
          description: `Conjunto de anúncios: ${record.adset_name || "Desconhecido"}`,
          action: "Otimizar segmentação",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Refine a segmentação e teste novos criativos");
      }

      if (record.reach && record.impressions) {
        const reachRatio = record.reach / record.impressions;
        if (reachRatio < thresholds.minReachToImpressions) {
          issues.push({
            id: `low-reach-ratio-${record.adset_id || Date.now()}`,
            severity: "info",
            message: `Proporção reach/impressions baixa: ${(reachRatio * 100).toFixed(1)}%`,
            description: "Possível fadiga de audiência detectada",
            action: "Expandir audiência",
            timestamp: new Date().toISOString(),
          });
          suggestions.push("Considere expandir ou renovar a audiência");
        }
      }

      // Validação de frequência (fadiga de criativo)
      if (record.frequency && record.frequency > 4) {
        const severity = record.frequency > 6 ? "critical" : "warning";
        issues.push({
          id: `high-frequency-${record.adset_id || Date.now()}`,
          severity,
          message: `Frequência muito alta: ${record.frequency.toFixed(1)}`,
          description: "Fadiga de criativo detectada",
          action: "Renovar criativos urgentemente",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Crie novos criativos ou pause temporariamente o conjunto de anúncios",
        );
      }

      // Validação de CTR (Click-through rate)
      if (record.ctr && record.ctr < 0.01) {
        // CTR menor que 1%
        issues.push({
          id: `low-ctr-meta-${record.adset_id || Date.now()}`,
          severity: "warning",
          message: `CTR muito baixo: ${(record.ctr * 100).toFixed(2)}%`,
          description: "Criativos podem não estar sendo atrativos",
          action: "Testar novos criativos",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Teste diferentes formatos, textos e call-to-actions");
      }

      // Validação de CPC (Cost per click)
      if (record.cpc && record.cpc > 5) {
        // CPC alto para Meta Ads
        issues.push({
          id: `high-cpc-meta-${record.adset_id || Date.now()}`,
          severity: "warning",
          message: `CPC alto: R$ ${record.cpc.toFixed(2)}`,
          description: "Custo por clique acima da média",
          action: "Otimizar lances e audiência",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Revise a estratégia de lance e refine a audiência");
      }

      // Validação de relevância score (se disponível)
      if (record.relevance_score && record.relevance_score < 6) {
        issues.push({
          id: `low-relevance-${record.ad_id || Date.now()}`,
          severity: "critical",
          message: `Score de relevância baixo: ${record.relevance_score}/10`,
          description: "Anúncio pode estar sendo considerado irrelevante",
          action: "Revisar criativo e audiência",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Alinhe melhor o criativo com os interesses da audiência",
        );
      }

      // Validação de placement performance
      if (record.placement_breakdown) {
        const feedPerformance = record.placement_breakdown.find(
          (p) => p.placement === "feed",
        );
        const storiesPerformance = record.placement_breakdown.find(
          (p) => p.placement === "stories",
        );

        if (feedPerformance && storiesPerformance) {
          const feedCTR = feedPerformance.ctr || 0;
          const storiesCTR = storiesPerformance.ctr || 0;

          if (feedCTR > storiesCTR * 3) {
            issues.push({
              id: `poor-stories-performance-${Date.now()}`,
              severity: "info",
              message:
                "Performance em Stories significativamente menor que Feed",
              action: "Otimizar criativos para Stories",
              timestamp: new Date().toISOString(),
            });
            suggestions.push(
              "Crie criativos específicos para Stories com formato vertical",
            );
          }
        }
      }

      // Validação de demographics performance
      if (record.demographics_breakdown) {
        const ageGroups = record.demographics_breakdown;
        let bestPerformingAge: string | null = null;
        let worstPerformingAge: string | null = null;
        let bestCTR = 0;
        let worstCTR = Infinity;

        ageGroups.forEach((group) => {
          const ctr = group.ctr ?? 0;
          const ageRange = group.age_range ?? null;
          if (ctr > bestCTR) {
            bestCTR = ctr;
            bestPerformingAge = ageRange;
          }
          if (ctr < worstCTR) {
            worstCTR = ctr;
            worstPerformingAge = ageRange;
          }
        });

        if (bestCTR > worstCTR * 3) {
          issues.push({
            id: `demographic-optimization-${Date.now()}`,
            severity: "info",
            message: `Faixa etária ${bestPerformingAge} performa ${(bestCTR / worstCTR).toFixed(1)}x melhor`,
            action: "Considerar refocar audiência",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Considere criar campanhas específicas para faixas etárias de alta performance",
          );
        }
      }
    }

    // Validações agregadas
    const avgEngagementRate =
      engagementRates.length > 0
        ? engagementRates.reduce((a, b) => a + b, 0) / engagementRates.length
        : 0;

    const avgFrequency =
      creativeFatigue.length > 0
        ? creativeFatigue.reduce((a, b) => a + b, 0) / creativeFatigue.length
        : 0;

    const overallCTR =
      totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const overallCPC = totalClicks > 0 ? totalSpend / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? totalConversions / totalClicks : 0;
    const reachRatio = totalImpressions > 0 ? totalReach / totalImpressions : 0;

    // Análise geral de performance
    if (avgEngagementRate < 0.02) {
      // Menos de 2%
      issues.push({
        id: `overall-low-engagement-${Date.now()}`,
        severity: "critical",
        message: `Engajamento geral muito baixo: ${(avgEngagementRate * 100).toFixed(2)}%`,
        action: "Revisão completa de estratégia",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Realize uma revisão completa de criativos, audiências e objetivos",
      );
    }

    if (avgFrequency > 5) {
      issues.push({
        id: `overall-high-frequency-${Date.now()}`,
        severity: "critical",
        message: `Frequência média muito alta: ${avgFrequency.toFixed(1)}`,
        action: "Renovação geral de criativos necessária",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Implemente um cronograma regular de renovação de criativos",
      );
    }

    if (overallCTR < 0.008) {
      // CTR geral menor que 0.8%
      issues.push({
        id: `overall-low-ctr-${Date.now()}`,
        severity: "warning",
        message: `CTR geral baixo: ${(overallCTR * 100).toFixed(2)}%`,
        action: "Melhorar atratividade dos anúncios",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Teste diferentes hooks, CTAs e formatos visuais");
    }

    if (conversionRate < 0.01) {
      // Taxa de conversão menor que 1%
      issues.push({
        id: `low-conversion-rate-meta-${Date.now()}`,
        severity: "critical",
        message: `Taxa de conversão baixa: ${(conversionRate * 100).toFixed(2)}%`,
        action: "Otimizar funil de conversão",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Revise landing pages e processo de conversão");
    }

    if (reachRatio < 0.7) {
      // Reach menor que 70% das impressões
      issues.push({
        id: `overall-low-reach-${Date.now()}`,
        severity: "warning",
        message: `Reach geral baixo: ${(reachRatio * 100).toFixed(1)}% das impressões`,
        action: "Expandir estratégia de audiência",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Considere lookalike audiences e interesse mais amplos");
    }

    // Análise de distribuição de objetivos
    const totalAdSets = Object.values(adSetsByObjective).reduce(
      (a, b) => a + b,
      0,
    );
    if (totalAdSets > 0) {
      const conversionAdSets = adSetsByObjective["CONVERSIONS"] || 0;
      const trafficAdSets = adSetsByObjective["TRAFFIC"] || 0;

      if (trafficAdSets > conversionAdSets * 2) {
        issues.push({
          id: `objective-distribution-${Date.now()}`,
          severity: "info",
          message: "Muitos conjuntos focados em tráfego vs conversões",
          action: "Considerar rebalancear objetivos",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Teste mais conjuntos com objetivo de conversão para melhor ROI",
        );
      }
    }
  }

  /**
   * Validações específicas para Search Console
   */
  private async validateSearchConsoleData(
    data: HealthMetricRecord[],
    issues: HealthIssue[],
    suggestions: string[],
    thresholds: ValidationThresholds["search_console"],
  ): Promise<void> {
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalQueries = 0;
    let totalPages = 0;
    const positions: number[] = [];
    const ctrs: number[] = [];
    const queriesByPosition: { [key: string]: number[] } = {};
    let coreWebVitalsIssues = 0;
    let indexingIssues = 0;

    // Análise agregada dos dados
    for (const record of data) {
      totalImpressions += record.impressions || 0;
      totalClicks += record.clicks || 0;

      if (record.query) totalQueries++;
      if (record.page) totalPages++;
      if (record.position) positions.push(record.position);
      if (record.ctr != null) ctrs.push(record.ctr);

      // Agrupar posições por faixas
      if (record.position) {
        const positionRange =
          record.position <= 3
            ? "1-3"
            : record.position <= 10
              ? "4-10"
              : record.position <= 20
                ? "11-20"
                : "20+";
        if (!queriesByPosition[positionRange])
          queriesByPosition[positionRange] = [];
        queriesByPosition[positionRange].push(record.position);
      }

      // Core Web Vitals issues
      if (record.core_web_vitals && record.core_web_vitals.status !== "GOOD") {
        coreWebVitalsIssues++;
      }

      // Indexing issues
      if (
        record.indexing_status &&
        record.indexing_status !== "Submitted and indexed"
      ) {
        indexingIssues++;
      }

      // Validações por registro individual
      // `!= null`, e não verdade: CTR zero é o pior caso, e não a ausência dele.
      if (record.ctr != null && record.ctr < thresholds.minCtr) {
        const position = record.position ?? Infinity;
        const severity = position <= 10 ? "critical" : "warning";
        issues.push({
          id: `seo-low-ctr-${record.query || record.page || Date.now()}`,
          severity,
          message: `CTR orgânico baixo: ${(record.ctr * 100).toFixed(2)}%`,
          description: record.query
            ? `Query: ${record.query}`
            : `Página: ${record.page}`,
          action: "Otimizar títulos e descrições",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Melhore titles e meta descriptions das páginas");
      }

      if (record.position && record.position > thresholds.maxPosition) {
        const severity = record.position > 50 ? "critical" : "warning";
        issues.push({
          id: `poor-ranking-${record.query || record.page || Date.now()}`,
          severity,
          message: `Posição média baixa: ${record.position.toFixed(1)}`,
          description: record.query
            ? `Query: ${record.query}`
            : `Página: ${record.page}`,
          action: "Melhorar SEO das páginas",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Trabalhe na otimização on-page e link building");
      }

      // Validação de impressões vs cliques (potencial perdido)
      const impressions = record.impressions ?? 0;
      const clicks = record.clicks ?? 0;
      if (impressions > 1000 && clicks < 10) {
        issues.push({
          id: `high-impressions-low-clicks-${Date.now()}`,
          severity: "warning",
          message: `Muitas impressões (${impressions}) mas poucos cliques (${clicks})`,
          description: record.query
            ? `Query: ${record.query}`
            : `Página: ${record.page}`,
          action: "Otimizar snippets e títulos",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Melhore o title e meta description para aumentar a atratividade",
        );
      }

      // Validação de queda de ranking
      if (record.position_change && record.position_change > 5) {
        issues.push({
          id: `ranking-drop-${Date.now()}`,
          severity: "critical",
          message: `Queda significativa no ranking: +${record.position_change.toFixed(1)} posições`,
          description: record.query
            ? `Query: ${record.query}`
            : `Página: ${record.page}`,
          action: "Investigar causa da queda",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Analise mudanças recentes na página e possível penalização",
        );
      }

      // Validação de palavras-chave com alto volume e baixa posição
      if (impressions > 5000 && (record.position ?? 0) > 20) {
        const position = record.position ?? 0;
        issues.push({
          id: `high-volume-poor-position-${Date.now()}`,
          severity: "critical",
          message: `Query com alto volume (${impressions} imp.) em posição baixa (${position.toFixed(1)})`,
          description: `Query: ${record.query}`,
          action: "Priorizar otimização desta query",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Esta é uma oportunidade de alto impacto - foque na otimização",
        );
      }

      // Validação de Core Web Vitals específicos
      if (record.core_web_vitals) {
        const cwv = record.core_web_vitals;
        const lcp = cwv.lcp;
        const fid = cwv.fid;
        const cls = cwv.cls;

        if (lcp != null && lcp > 2.5) {
          issues.push({
            id: `poor-lcp-page-${Date.now()}`,
            severity: "critical",
            message: `LCP ruim: ${lcp.toFixed(1)}s`,
            description: `Página: ${record.page}`,
            action: "Otimizar velocidade de carregamento",
            timestamp: new Date().toISOString(),
          });
        }

        if (fid != null && fid > 100) {
          issues.push({
            id: `poor-fid-page-${Date.now()}`,
            severity: "warning",
            message: `FID alto: ${fid}ms`,
            description: `Página: ${record.page}`,
            action: "Otimizar JavaScript",
            timestamp: new Date().toISOString(),
          });
        }

        if (cls != null && cls > 0.1) {
          issues.push({
            id: `poor-cls-page-${Date.now()}`,
            severity: "warning",
            message: `CLS alto: ${cls.toFixed(3)}`,
            description: `Página: ${record.page}`,
            action: "Estabilizar layout",
            timestamp: new Date().toISOString(),
          });
        }
      }

      // Validação de mobile usability
      if (
        record.mobile_usability_issues &&
        record.mobile_usability_issues.length > 0
      ) {
        issues.push({
          id: `mobile-usability-${Date.now()}`,
          severity: "warning",
          message: `${record.mobile_usability_issues.length} problema(s) de usabilidade mobile`,
          description: `Página: ${record.page}`,
          action: "Corrigir problemas mobile",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Melhore a responsividade e experiência mobile");
      }
    }

    // Validações agregadas
    const avgPosition =
      positions.length > 0
        ? positions.reduce((a, b) => a + b, 0) / positions.length
        : 0;

    const avgCTR =
      ctrs.length > 0 ? ctrs.reduce((a, b) => a + b, 0) / ctrs.length : 0;

    const overallCTR =
      totalImpressions > 0 ? totalClicks / totalImpressions : 0;

    // Análise de distribuição de posições
    const top3Queries = queriesByPosition["1-3"]?.length || 0;
    const top10Queries =
      (queriesByPosition["1-3"]?.length || 0) +
      (queriesByPosition["4-10"]?.length || 0);
    const totalQueriesWithPosition = positions.length;

    if (totalQueriesWithPosition > 0) {
      const top10Percentage = (top10Queries / totalQueriesWithPosition) * 100;

      if (top10Percentage < 20) {
        issues.push({
          id: `low-top10-presence-${Date.now()}`,
          severity: "critical",
          message: `Apenas ${top10Percentage.toFixed(1)}% das queries no top 10`,
          action: "Foco em SEO on-page",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Concentre esforços em otimização on-page para melhorar rankings",
        );
      }

      const top3Percentage = (top3Queries / totalQueriesWithPosition) * 100;
      if (top3Percentage < 5) {
        issues.push({
          id: `low-top3-presence-${Date.now()}`,
          severity: "warning",
          message: `Apenas ${top3Percentage.toFixed(1)}% das queries no top 3`,
          action: "Otimizar para posições destacadas",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Foque em featured snippets e posição zero");
      }
    }

    // Análise de Core Web Vitals geral
    if (coreWebVitalsIssues > totalPages * 0.3) {
      issues.push({
        id: `widespread-cwv-issues-${Date.now()}`,
        severity: "critical",
        message: `${coreWebVitalsIssues} páginas com problemas de Core Web Vitals`,
        action: "Auditoria técnica necessária",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Realize uma auditoria técnica completa do site");
    }

    // Análise de indexação
    if (indexingIssues > totalPages * 0.1) {
      issues.push({
        id: `indexing-issues-${Date.now()}`,
        severity: "critical",
        message: `${indexingIssues} páginas com problemas de indexação`,
        action: "Resolver problemas de indexação",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Revise sitemap, robots.txt e status de indexação");
    }

    // Análise de CTR geral
    if (overallCTR < 0.02) {
      // CTR menor que 2%
      issues.push({
        id: `overall-low-ctr-seo-${Date.now()}`,
        severity: "critical",
        message: `CTR orgânico geral muito baixo: ${(overallCTR * 100).toFixed(2)}%`,
        action: "Otimização de snippets urgente",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Implemente uma estratégia abrangente de otimização de titles e descriptions",
      );
    }

    // Análise de posição média geral
    if (avgPosition > 25) {
      issues.push({
        id: `poor-avg-position-${Date.now()}`,
        severity: "critical",
        message: `Posição média muito baixa: ${avgPosition.toFixed(1)}`,
        action: "Estratégia SEO abrangente necessária",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Implemente uma estratégia SEO completa focada em conteúdo e autoridade",
      );
    }

    // Oportunidades identificadas
    const highImpressionLowRankQueries = data.filter((r) => {
      const impressions = r.impressions ?? 0;
      const position = r.position ?? 0;
      return impressions > 1000 && position > 10 && position <= 20;
    });
    if (highImpressionLowRankQueries.length > 0) {
      issues.push({
        id: `seo-opportunities-${Date.now()}`,
        severity: "info",
        message: `${highImpressionLowRankQueries.length} oportunidades identificadas (queries na página 2)`,
        action: "Priorizar otimização",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Queries na página 2 com alto volume são oportunidades de quick wins",
      );
    }
  }

  /**
   * Validações específicas para Instagram Insights
   */
  private async validateInstagramData(
    data: HealthMetricRecord[],
    issues: HealthIssue[],
    suggestions: string[],
    thresholds: ValidationThresholds["instagram_insights"],
  ): Promise<void> {
    let totalFollowers = 0;
    let totalPosts = 0;
    let totalStories = 0;
    let totalReels = 0;
    const engagementRates: number[] = [];
    const storiesCompletionRates: number[] = [];
    const postTypes: { [key: string]: number } = {};
    const followerGrowth: number[] = [];
    const hashtagPerformance: { [key: string]: number } = {};

    // Análise agregada dos dados
    for (const record of data) {
      if (record.followers_count) {
        totalFollowers = Math.max(totalFollowers, record.followers_count);
        if (record.followers_change)
          followerGrowth.push(record.followers_change);
      }

      if (record.post_type) {
        postTypes[record.post_type] = (postTypes[record.post_type] || 0) + 1;

        switch (record.post_type) {
          case "post":
            totalPosts++;
            break;
          case "story":
            totalStories++;
            break;
          case "reel":
            totalReels++;
            break;
        }
      }

      if (record.engagement_rate) engagementRates.push(record.engagement_rate);
      if (record.stories_completion_rate)
        storiesCompletionRates.push(record.stories_completion_rate);

      // Análise de hashtags
      const hashtagEngagementRate = record.engagement_rate;
      if (record.hashtags && hashtagEngagementRate != null) {
        record.hashtags.forEach((tag: string) => {
          if (!hashtagPerformance[tag]) hashtagPerformance[tag] = 0;
          hashtagPerformance[tag] += hashtagEngagementRate;
        });
      }

      // Validações por registro individual
      const engagementRate = record.engagement_rate;
      if (
        engagementRate != null &&
        engagementRate < thresholds.minEngagementRate
      ) {
        const severity = engagementRate < 0.01 ? "critical" : "warning";
        issues.push({
          id: `ig-low-engagement-${record.post_id || Date.now()}`,
          severity,
          message: `Engajamento baixo: ${(engagementRate * 100).toFixed(2)}%`,
          description: record.post_type
            ? `Tipo: ${record.post_type}`
            : "Post do Instagram",
          action: "Revisar estratégia de conteúdo",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Teste novos tipos de conteúdo e horários de postagem",
        );
      }

      if (
        record.stories_completion_rate &&
        record.stories_completion_rate < thresholds.minStoriesCompletionRate
      ) {
        issues.push({
          id: `stories-low-completion-${record.story_id || Date.now()}`,
          severity: "info",
          message: `Taxa de conclusão de stories baixa: ${(record.stories_completion_rate * 100).toFixed(1)}%`,
          description: "Stories podem estar muito longas ou não envolventes",
          action: "Melhorar storytelling",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Crie stories mais envolventes e interativos");
      }

      // Validação de alcance vs impressões
      if (record.reach && record.impressions) {
        const reachRatio = record.reach / record.impressions;
        if (reachRatio < 0.6) {
          // Reach menor que 60% das impressões
          issues.push({
            id: `low-reach-ratio-ig-${Date.now()}`,
            severity: "warning",
            message: `Baixo alcance único: ${(reachRatio * 100).toFixed(1)}% das impressões`,
            description:
              "Conteúdo sendo mostrado repetidamente para mesmos usuários",
            action: "Diversificar audiência",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Use hashtags mais diversificadas para alcançar novos usuários",
          );
        }
      }

      // Validação de salvamentos vs curtidas (indicador de qualidade)
      if (record.saves && record.likes) {
        const saveRate = record.saves / record.likes;
        if (saveRate > 0.1) {
          // Taxa de salvamento acima de 10%
          issues.push({
            id: `high-save-rate-${Date.now()}`,
            severity: "info",
            message: `Excelente taxa de salvamentos: ${(saveRate * 100).toFixed(1)}%`,
            description: "Conteúdo de alta qualidade detectado",
            action: "Replicar este tipo de conteúdo",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Este formato de conteúdo está sendo muito valorizado - crie mais similar",
          );
        }
      }

      // Validação de comentários vs curtidas (engajamento profundo)
      if (record.comments && record.likes) {
        const commentRate = record.comments / record.likes;
        if (commentRate < 0.01) {
          // Menos de 1% de comentários
          issues.push({
            id: `low-comment-rate-${Date.now()}`,
            severity: "warning",
            message: `Baixa taxa de comentários: ${(commentRate * 100).toFixed(2)}%`,
            description: "Pouco engajamento ativo da audiência",
            action: "Incentivar interação",
            timestamp: new Date().toISOString(),
          });
          suggestions.push("Faça perguntas e incentive comentários nos posts");
        }
      }

      // Validação de performance de Reels
      if (record.post_type === "reel") {
        if (record.plays && record.impressions) {
          const playRate = record.plays / record.impressions;
          if (playRate < 0.3) {
            // Menos de 30% de reprodução
            issues.push({
              id: `low-reel-play-rate-${Date.now()}`,
              severity: "warning",
              message: `Baixa taxa de reprodução do Reel: ${(playRate * 100).toFixed(1)}%`,
              description:
                "Thumbnail ou início do vídeo pode não estar atrativo",
              action: "Melhorar thumbnail e hook inicial",
              timestamp: new Date().toISOString(),
            });
            suggestions.push(
              "Melhore o thumbnail e os primeiros 3 segundos do Reel",
            );
          }
        }

        if (record.shares && record.plays) {
          const shareRate = record.shares / record.plays;
          if (shareRate > 0.05) {
            // Mais de 5% de shares
            issues.push({
              id: `viral-reel-potential-${Date.now()}`,
              severity: "info",
              message: `Reel com potencial viral: ${(shareRate * 100).toFixed(1)}% de shares`,
              description: "Conteúdo sendo amplamente compartilhado",
              action: "Investir em promoção",
              timestamp: new Date().toISOString(),
            });
            suggestions.push(
              "Considere impulsionar este Reel para alcançar ainda mais pessoas",
            );
          }
        }
      }

      // Validação de timing de postagem
      if (record.post_hour && record.engagement_rate) {
        const isOptimalTime =
          (record.post_hour >= 18 && record.post_hour <= 21) ||
          (record.post_hour >= 11 && record.post_hour <= 14);

        if (
          !isOptimalTime &&
          record.engagement_rate < thresholds.minEngagementRate
        ) {
          issues.push({
            id: `suboptimal-timing-${Date.now()}`,
            severity: "info",
            message: `Post fora do horário ideal: ${record.post_hour}h`,
            description: "Horário pode estar impactando o alcance",
            action: "Testar horários de pico",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Teste postar entre 11h-14h ou 18h-21h para melhor alcance",
          );
        }
      }

      // Validação de uso de hashtags
      if (record.hashtags) {
        const hashtagCount = record.hashtags.length;

        if (hashtagCount < 5) {
          issues.push({
            id: `few-hashtags-${Date.now()}`,
            severity: "info",
            message: `Poucas hashtags utilizadas: ${hashtagCount}`,
            description: "Perdendo oportunidades de descoberta",
            action: "Usar mais hashtags relevantes",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Use até 30 hashtags relevantes para maximizar a descoberta",
          );
        }

        if (hashtagCount > 30) {
          issues.push({
            id: `too-many-hashtags-${Date.now()}`,
            severity: "warning",
            message: `Muitas hashtags utilizadas: ${hashtagCount}`,
            description: "Pode parecer spam para o algoritmo",
            action: "Reduzir e focar em relevância",
            timestamp: new Date().toISOString(),
          });
          suggestions.push(
            "Mantenha entre 20-30 hashtags super relevantes ao conteúdo",
          );
        }
      }

      // Validação de consistência de posting
      if (record.days_since_last_post && record.days_since_last_post > 3) {
        const severity = record.days_since_last_post > 7 ? "warning" : "info";
        issues.push({
          id: `posting-gap-${Date.now()}`,
          severity,
          message: `Gap de postagem: ${record.days_since_last_post} dias`,
          description: "Algoritmo favorece consistência",
          action: "Manter frequência regular",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Mantenha uma frequência consistente de postagem");
      }
    }

    // Validações agregadas
    const avgEngagementRate =
      engagementRates.length > 0
        ? engagementRates.reduce((a, b) => a + b, 0) / engagementRates.length
        : 0;

    const avgStoriesCompletion =
      storiesCompletionRates.length > 0
        ? storiesCompletionRates.reduce((a, b) => a + b, 0) /
          storiesCompletionRates.length
        : 0;

    const avgFollowerGrowth =
      followerGrowth.length > 0
        ? followerGrowth.reduce((a, b) => a + b, 0) / followerGrowth.length
        : 0;

    // Análise geral de engajamento
    if (avgEngagementRate < 0.01) {
      // Menos de 1%
      issues.push({
        id: `overall-low-engagement-ig-${Date.now()}`,
        severity: "critical",
        message: `Engajamento geral muito baixo: ${(avgEngagementRate * 100).toFixed(2)}%`,
        action: "Revisão completa de estratégia",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Considere uma mudança na estratégia de conteúdo e audiência",
      );
    }

    // Análise de crescimento de seguidores
    if (avgFollowerGrowth < 0) {
      issues.push({
        id: `follower-decline-${Date.now()}`,
        severity: "critical",
        message: `Perda de seguidores: ${avgFollowerGrowth.toFixed(0)} por período`,
        action: "Investigar causa da perda",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Analise o conteúdo recente e feedback da audiência");
    } else if (avgFollowerGrowth < 10 && totalFollowers > 1000) {
      issues.push({
        id: `low-follower-growth-${Date.now()}`,
        severity: "warning",
        message: `Crescimento baixo: +${avgFollowerGrowth.toFixed(0)} seguidores por período`,
        action: "Aumentar estratégias de crescimento",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Implemente estratégias de crescimento orgânico e colaborações",
      );
    }

    // Análise de distribuição de conteúdo
    const totalContent = totalPosts + totalStories + totalReels;
    if (totalContent > 0) {
      const reelPercentage = (totalReels / totalContent) * 100;

      if (reelPercentage < 30) {
        issues.push({
          id: `low-reels-usage-${Date.now()}`,
          severity: "warning",
          message: `Poucos Reels: ${reelPercentage.toFixed(1)}% do conteúdo`,
          action: "Aumentar produção de Reels",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Reels têm maior alcance - aumente sua produção");
      }

      const storyPercentage = (totalStories / totalContent) * 100;
      if (storyPercentage < 40) {
        issues.push({
          id: `low-stories-usage-${Date.now()}`,
          severity: "info",
          message: `Poucas Stories: ${storyPercentage.toFixed(1)}% do conteúdo`,
          action: "Aumentar frequência de Stories",
          timestamp: new Date().toISOString(),
        });
        suggestions.push("Stories mantêm a audiência engajada diariamente");
      }
    }

    // Análise de performance de hashtags
    const sortedHashtags = Object.entries(hashtagPerformance)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    if (sortedHashtags.length > 0) {
      const bestHashtag = sortedHashtags[0];
      const worstHashtag = sortedHashtags[sortedHashtags.length - 1];

      if (bestHashtag[1] > worstHashtag[1] * 2) {
        issues.push({
          id: `hashtag-optimization-opportunity-${Date.now()}`,
          severity: "info",
          message: `Hashtag "${bestHashtag[0]}" performa ${(bestHashtag[1] / worstHashtag[1]).toFixed(1)}x melhor`,
          action: "Otimizar estratégia de hashtags",
          timestamp: new Date().toISOString(),
        });
        suggestions.push(
          "Foque em hashtags similares às de melhor performance",
        );
      }
    }

    // Análise de qualidade vs quantidade
    if (totalContent > 20 && avgEngagementRate < 0.02) {
      issues.push({
        id: `quality-over-quantity-${Date.now()}`,
        severity: "warning",
        message: "Alto volume de conteúdo mas baixo engajamento",
        action: "Focar em qualidade sobre quantidade",
        timestamp: new Date().toISOString(),
      });
      suggestions.push(
        "Reduza a frequência e foque em conteúdo de maior qualidade",
      );
    }

    // Validação de Stories completion rate geral
    if (avgStoriesCompletion < 0.7) {
      issues.push({
        id: `overall-low-stories-completion-${Date.now()}`,
        severity: "warning",
        message: `Taxa de conclusão de Stories baixa: ${(avgStoriesCompletion * 100).toFixed(1)}%`,
        action: "Melhorar estrutura das Stories",
        timestamp: new Date().toISOString(),
      });
      suggestions.push("Crie Stories mais concisas e com hooks mais fortes");
    }
  }

  /**
   * Métodos auxiliares
   */
  private async checkConnectivity(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ValidationResult> {
    const issues: HealthIssue[] = [];
    let score = 100;

    try {
      console.log(`🔍 Verificando conectividade de ${integrationType}`);

      const data = await this.getConnectionStatus(integrationType);

      if (!data.is_connected) {
        issues.push({
          id: `disconnected-${Date.now()}`,
          severity: "critical",
          message: "Integração desconectada",
          description: "A integração não está conectada ao serviço",
          action: "Reconectar integração",
          timestamp: new Date().toISOString(),
        });
        score = 0;
      }

      console.log(`✅ ${integrationType} is_connected: ${data.is_connected}`);
      return { isValid: data.is_connected, score, issues, suggestions: [] };
    } catch (error) {
      console.error(`❌ Erro ao verificar ${integrationType}:`, error);

      issues.push({
        id: `connectivity-error-${Date.now()}`,
        severity: "critical",
        message: "Erro ao verificar conectividade",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        action: "Verificar configuração",
        timestamp: new Date().toISOString(),
      });

      return { isValid: false, score: 0, issues, suggestions: [] };
    }
  }

  private async getConnectionStatus(
    integrationType: IntegrationType,
  ): Promise<IntegrationStatusResponse> {
    switch (integrationType) {
      case "google_ads": {
        const { data } =
          await getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet({
            throwOnError: true,
          });
        return data;
      }
      case "google_analytics": {
        const { data } =
          await getGoogleAnalyticsStatusApiV1IntegrationsGoogleAnalyticsStatusGet(
            { throwOnError: true },
          );
        return data;
      }
      case "meta_ads": {
        const { data } =
          await getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet({
            throwOnError: true,
          });
        return data;
      }
      case "search_console": {
        const { data } =
          await getConnectionStatusApiV1IntegrationsSearchConsoleConnectionStatusGet(
            { throwOnError: true },
          );
        return data;
      }
      case "instagram_insights": {
        const { data } =
          await getStatusApiV1IntegrationsInstagramInsightsStatusGet({
            throwOnError: true,
          });
        return data;
      }
    }
  }

  private async checkDataFreshness(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ValidationResult> {
    // Implementar verificação de freshness dos dados
    return { isValid: true, score: 100, issues: [], suggestions: [] };
  }

  private async checkPerformance(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ValidationResult> {
    // Implementar verificação de performance
    return { isValid: true, score: 100, issues: [], suggestions: [] };
  }

  private async fetchRecentData(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<HealthMetricRecord[]> {
    try {
      const endDate = new Date().toISOString().split("T")[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];

      switch (integrationType) {
        case "google_ads": {
          const { data: accounts } =
            await getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet({
              throwOnError: true,
            });
          const accountId = accounts[0]?.customer_id ?? accounts[0]?.account_id;
          if (!accountId) return [];

          const { data } =
            await getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet({
              query: { account_id: accountId },
              throwOnError: true,
            });
          return data;
        }
        case "google_analytics": {
          const { data: accounts } =
            await getGoogleAnalyticsAccountsApiV1IntegrationsGoogleAnalyticsAccountsGet(
              { throwOnError: true },
            );
          const accountId = accounts[0]?.external_account_id ?? accounts[0]?.id;
          if (!accountId) return [];

          const { data } =
            await getCompleteDataApiV1IntegrationsGoogleAnalyticsCompleteDataGet(
              {
                query: {
                  account_id: accountId,
                  start_date: startDate,
                  end_date: endDate,
                },
                throwOnError: true,
              },
            );
          return data.daily_metrics;
        }
        case "meta_ads": {
          const { data: accounts } =
            await getAccountsApiV1IntegrationsMetaAdsAccountsGet({
              throwOnError: true,
            });
          const accountId = accounts[0]?.external_account_id ?? accounts[0]?.id;
          if (!accountId) return [];

          const { data } =
            await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
              query: {
                account_id: accountId,
                start_date: startDate,
                end_date: endDate,
              },
              throwOnError: true,
            });
          return data.daily_metrics;
        }
        case "search_console": {
          const { data: accounts } =
            await getAccountsApiV1IntegrationsSearchConsoleAccountsGet({
              throwOnError: true,
            });
          const accountId =
            accounts[0]?.site_url ??
            accounts[0]?.external_account_id ??
            accounts[0]?.id;
          if (!accountId) return [];

          // A validação olha consulta a consulta, então a fonte é `top-queries`, que agrega
          // no recorte certo — `complete-data` é a série do site, um dia por linha (M-098).
          // `avg_ctr` vem em percentual e os limiares são fração.
          const { data } =
            await getTopQueriesApiV1IntegrationsSearchConsoleTopQueriesGet({
              query: {
                account_id: accountId,
                start_date: startDate,
                end_date: endDate,
                limit: 50,
              },
              throwOnError: true,
            });
          return data.map((consulta) => ({
            query: consulta.query,
            impressions: consulta.total_impressions,
            clicks: consulta.total_clicks,
            ctr: consulta.avg_ctr / 100,
            position: consulta.avg_position,
          }));
        }
        case "instagram_insights": {
          const { data: accounts } =
            await getAccountsApiV1IntegrationsInstagramInsightsAccountsGet({
              throwOnError: true,
            });
          const accountId = accounts[0]?.account_id;
          if (!accountId) return [];

          const { data } =
            await getCompleteDataApiV1IntegrationsInstagramInsightsCompleteDataGet(
              {
                query: {
                  account_id: accountId,
                  start_date: startDate,
                  end_date: endDate,
                },
                throwOnError: true,
              },
            );
          return data.daily_metrics;
        }
      }
    } catch (error) {
      console.error(`Erro ao buscar dados para ${integrationType}:`, error);
      return [];
    }
  }

  private async calculateDataQuality(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<number> {
    // Implementar cálculo de qualidade dos dados
    return 85;
  }

  private async calculateUptime(integrationId: string): Promise<number> {
    // Implementar cálculo de uptime
    return 99.5;
  }

  private emitHealthUpdate(
    integrationId: string,
    health: ConnectionHealth,
  ): void {
    // Implementar emissão de eventos para listeners
    window.dispatchEvent(
      new CustomEvent("integration-health-update", {
        detail: { integrationId, health },
      }),
    );
  }

  /**
   * API pública
   */
  getHealth(integrationId: string): ConnectionHealth | null {
    return this.healthCache.get(integrationId) || null;
  }

  getAllHealth(): Map<string, ConnectionHealth> {
    return new Map(this.healthCache);
  }

  clearCache(): void {
    this.healthCache.clear();
  }

  async forceHealthCheck(
    integrationType: IntegrationType,
    integrationId: string,
  ): Promise<ConnectionHealth> {
    const health = await this.checkIntegrationHealth(
      integrationType,
      integrationId,
    );
    this.healthCache.set(integrationId, health);
    this.emitHealthUpdate(integrationId, health);
    return health;
  }
}

// Funções de recovery
async function exponentialBackoffRetry(): Promise<void> {
  console.log("🔄 Aplicando exponential backoff retry...");
  // Implementar retry com backoff exponencial
}

async function respectRateLimitAndRetry(): Promise<void> {
  console.log("⏱️ Respeitando rate limit e reagendando...");
  // Implementar retry respeitando rate limit
}

async function refreshTokenAndRetry(): Promise<void> {
  console.log("🔑 Renovando token de acesso...");
  // Implementar renovação de token
}

async function forceSyncAndValidate(): Promise<void> {
  console.log("🔄 Forçando sincronização completa...");
  // Implementar sincronização forçada
}

async function redirectToReauth(): Promise<void> {
  console.log("🔐 Redirecionando para reautenticação...");
  // Implementar redirecionamento para auth
}

async function retryWithDelay(): Promise<void> {
  console.log("⏳ Tentando novamente com delay...");
  // Implementar retry com delay
}

// Instância singleton
export const integrationHealthService = new IntegrationHealthService();
